import { useState, useEffect } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Gift, X, ArrowRight } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client.safe'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface TrialWelcomeBannerProps {
  className?: string
}

export function TrialWelcomeBanner({ className }: TrialWelcomeBannerProps) {
  const [showBanner, setShowBanner] = useState(false)
  const [trialCredits, setTrialCredits] = useState(0)
  const navigate = useNavigate()
  const { t } = useTranslation('common')

  useEffect(() => {
    checkTrialStatus()
  }, [])

  const checkTrialStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: billingAccount } = await supabase
        .from('billing_accounts')
        .select('trial_granted, trial_credits, trial_granted_at')
        .eq('user_id', user.id)
        .single()

      if (billingAccount?.trial_granted && billingAccount.trial_granted_at) {
        // Check if trial was granted recently (within last 7 days)
        const grantedDate = new Date(billingAccount.trial_granted_at)
        const daysSinceGranted = Math.floor((Date.now() - grantedDate.getTime()) / (1000 * 60 * 60 * 24))
        
        // Check if banner has been dismissed in sessionStorage
        const bannerDismissed = sessionStorage.getItem(`trial_banner_dismissed_${user.id}`)
        
        if (daysSinceGranted <= 7 && !bannerDismissed) {
          setTrialCredits(billingAccount.trial_credits)
          setShowBanner(true)
        }
      }
    } catch (error) {
      console.error('Error checking trial status:', error)
    }
  }

  const dismissBanner = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        sessionStorage.setItem(`trial_banner_dismissed_${user.id}`, 'true')
      }
    } catch (error) {
      console.error('Error dismissing banner:', error)
    }
    setShowBanner(false)
  }

  const navigateToNewCase = () => {
    navigate('/cases/new')
    dismissBanner()
  }

  if (!showBanner) return null

  return (
    <Alert className={`border-success bg-success/10 ${className}`}>
      <Gift className="h-4 w-4 text-success" />
      <AlertDescription className="flex items-center justify-between w-full">
        <div className="flex-1">
          <span className="font-medium text-success">
            {t('trial.welcome.title')} {t('trial.welcome.message', { count: trialCredits })}
          </span>
          <span className="text-sm text-muted-foreground ml-2">
            {t('trial.getStarted')}
          </span>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <Button
            variant="outline"
            size="sm"
            onClick={navigateToNewCase}
            className="gap-2 border-success/20 text-success hover:bg-success/10"
          >
            {t('cases.new')}
            <ArrowRight className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={dismissBanner}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  )
}